import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material"
import FolderIcon from "@mui/icons-material/FolderOutlined"
import UploadIcon from "@mui/icons-material/CloudUploadOutlined"
import NewFolderIcon from "@mui/icons-material/CreateNewFolderOutlined"
import GridIcon from "@mui/icons-material/GridViewOutlined"
import ListIcon from "@mui/icons-material/ViewListOutlined"
import MoreIcon from "@mui/icons-material/MoreVertOutlined"
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined"
import RenameIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined"
import MoveIcon from "@mui/icons-material/DriveFileMoveOutlined"
import ShareIcon from "@mui/icons-material/PersonAddAlt1Outlined"
import TrashIcon from "@mui/icons-material/DeleteOutlineOutlined"
import RestoreIcon from "@mui/icons-material/RestoreFromTrashOutlined"
import HomeIcon from "@mui/icons-material/HomeOutlined"
import PeopleIcon from "@mui/icons-material/GroupsOutlined"
import ImageGlyph from "@mui/icons-material/ImageOutlined"
import PdfGlyph from "@mui/icons-material/PictureAsPdfOutlined"
import SheetGlyph from "@mui/icons-material/TableChartOutlined"
import DocGlyph from "@mui/icons-material/DescriptionOutlined"
import ArchiveGlyph from "@mui/icons-material/FolderZipOutlined"
import FileGlyphDefault from "@mui/icons-material/InsertDriveFileOutlined"

import { clientApi, driveApi } from "../api"
import { downloadFile, fileKind, formatBytes } from "../api/uploads"
import { usePermissions } from "../permissions"
import { useAuth } from "../context/AuthContext"
import { useUndo } from "../context/UndoContext"
import PageHeader from "../components/PageHeader"
import EmptyState from "../components/EmptyState"
import { LoadingRows } from "../components/LoadingState"
import DropZone from "../components/files/DropZone"
import UserAvatar from "../components/UserAvatar"
import ClientSelect from "../components/ClientSelect"
import { formatDate } from "../utils/format"
import { INTERACTIVE_CARD, rise } from "../motion"
import { MOTION, transition } from "../theme"
import ShareDialog from "./drive/ShareDialog"
import MoveDialog from "./drive/MoveDialog"

/**
 * The document store.
 *
 * ── Why a folder tree rather than a list with tags ──────────────────────────
 *
 * Everything an organization keeps that belongs to no task — the signed
 * contract, the W-9, the carrier's rate sheet — was living in somebody's email,
 * and the only way to give one to a colleague was to send it again. A folder is
 * the unit people already share, hand over and archive: "give the new
 * accountant the Payroll folder" is one act. Search covers retrieval, which is
 * the half tags would have been better at.
 *
 * ── Why the root is not a folder ────────────────────────────────────────────
 *
 * It is two lists: yours, and what was shared with you. That is not a
 * simplification of a tree, it is what the root actually is — there is no
 * container everybody is inside, and pretending there is makes "who can see
 * this" unanswerable at the top level.
 */

const ICON_TINT = {
  image: "#2f86e0",
  pdf: "#e04b3c",
  sheet: "#0c7f5c",
  doc: "#4650db",
  archive: "#e9a13b",
  file: "#8a8f98",
}

export default function DrivePage() {
  const { can, isBackOffice } = usePermissions()
  const { user } = useAuth()
  const { offer } = useUndo()

  const [params, setParams] = useSearchParams()
  const folderId = Number(params.get("folder") || 0)
  const clientId = params.get("client") || ""

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [view, setView] = useState(() => localStorage.getItem("tallywise.drive.view") || "grid")
  const [search, setSearch] = useState("")
  const [results, setResults] = useState(null)
  const [mode, setMode] = useState("browse") // browse | trash
  const [trash, setTrash] = useState(null)
  const [uploads, setUploads] = useState([])
  const [menu, setMenu] = useState(null)
  const [renaming, setRenaming] = useState(null)
  const [sharing, setSharing] = useState(null)
  const [moving, setMoving] = useState(null)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState("")
  const [clients, setClients] = useState([])

  const fileInput = useRef(null)

  const scope = useMemo(() => (clientId ? { client_id: clientId } : {}), [clientId])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const payload = await driveApi.list({ ...scope, folder: folderId })
      setData(payload)
      setError(null)
    } catch (err) {
      setError(err.userMessage || "That folder could not be opened.")
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [scope, folderId])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!isBackOffice || !can("clients", "view")) return
    clientApi
      .list()
      .then((payload) => setClients(payload.organizations || []))
      .catch(() => setClients([]))
  }, [isBackOffice, can])

  useEffect(() => {
    localStorage.setItem("tallywise.drive.view", view)
  }, [view])

  /* Search is its own result set rather than a filter over the listing: what
     matches is spread across folders, and showing it inside the current one
     would imply the matches live here. */
  useEffect(() => {
    const term = search.trim()
    if (term.length < 2) {
      setResults(null)
      return undefined
    }
    const timer = setTimeout(() => {
      driveApi
        .search({ ...scope, q: term })
        .then(setResults)
        .catch(() => setResults({ folders: [], files: [] }))
    }, 300)
    return () => clearTimeout(timer)
  }, [search, scope])

  const openTrash = async () => {
    setMode("trash")
    setTrash(null)
    try {
      setTrash(await driveApi.trash(scope))
    } catch (err) {
      setError(err.userMessage || "The trash could not be opened.")
      setTrash({ folders: [], files: [] })
    }
  }

  const goTo = (id) => {
    setMode("browse")
    setSearch("")
    const next = new URLSearchParams(params)
    if (id) next.set("folder", String(id))
    else next.delete("folder")
    setParams(next, { replace: false })
  }

  const pickClient = (id) => {
    const next = new URLSearchParams()
    if (id) next.set("client", id)
    setParams(next, { replace: false })
  }

  /*
   * Uploads run one at a time and report their own progress.
   *
   * In parallel they compete for the same connection and the individual
   * percentages go backwards; one after another, the number on the screen is
   * the number that is happening. Each finishes on its own, so a rejected file
   * in the middle does not take the rest of the batch with it.
   */
  const uploadFiles = useCallback(
    async (files) => {
      if (!can("drive", "upload")) return
      for (const file of files) {
        const ticket = { name: file.name, percent: 0, id: `${file.name}-${Date.now()}-${Math.random()}` }
        setUploads((current) => [...current, ticket])
        try {
          await driveApi.upload(file, {
            folderId,
            clientId: clientId || undefined,
            onProgress: (percent) =>
              setUploads((current) => current.map((row) => (row.id === ticket.id ? { ...row, percent } : row))),
          })
        } catch (err) {
          setError(`${file.name}: ${err.userMessage || "could not be uploaded"}`)
        } finally {
          setUploads((current) => current.filter((row) => row.id !== ticket.id))
        }
      }
      load()
    },
    [can, folderId, clientId, load],
  )

  const createFolder = async () => {
    const name = newName.trim()
    if (!name) return
    try {
      await driveApi.createFolder({ ...scope, parent_id: folderId, name })
      setCreating(false)
      setNewName("")
      load()
    } catch (err) {
      setError(err.userMessage || "That folder could not be created.")
    }
  }

  const rename = async () => {
    const name = renaming?.draft?.trim()
    if (!name || !renaming) return
    try {
      if (renaming.item.kind === "folder") await driveApi.updateFolder(renaming.item.id, { ...scope, name })
      else await driveApi.updateFile(renaming.item.id, { ...scope, name })
      setRenaming(null)
      load()
      if (mode === "trash") openTrash()
    } catch (err) {
      setError(err.userMessage || "That could not be renamed.")
    }
  }

  /* Trash with an undo, because "are you sure" on a reversible action is a
     dialog people learn to dismiss without reading. The irreversible one — the
     purge inside the trash — does ask. */
  const trashItem = async (item) => {
    try {
      if (item.kind === "folder") await driveApi.removeFolder(item.id, scope)
      else await driveApi.removeFile(item.id, scope)
      load()
      offer(`"${item.name}" moved to the trash`, async () => {
        if (item.kind === "folder") await driveApi.restoreFolder(item.id, scope)
        else await driveApi.restoreFile(item.id, scope)
        load()
      })
    } catch (err) {
      setError(err.userMessage || "That could not be moved to the trash.")
    }
  }

  const restore = async (item) => {
    try {
      if (item.kind === "folder") await driveApi.restoreFolder(item.id, scope)
      else await driveApi.restoreFile(item.id, scope)
      setNotice(`"${item.name}" is back where it was.`)
      openTrash()
    } catch (err) {
      setError(err.userMessage || "That could not be restored.")
    }
  }

  const purge = async (item) => {
    const warning =
      item.kind === "folder"
        ? `Delete "${item.name}" and everything inside it? This cannot be undone.`
        : `Delete "${item.name}" for good? This cannot be undone.`
    if (!window.confirm(warning)) return
    try {
      if (item.kind === "folder") await driveApi.removeFolder(item.id, { ...scope, purge: true })
      else await driveApi.removeFile(item.id, { ...scope, purge: true })
      setNotice(`"${item.name}" is gone.`)
      openTrash()
    } catch (err) {
      setError(err.userMessage || "That could not be deleted.")
    }
  }

  const open = (item) => {
    if (item.kind === "folder") return goTo(item.id)
    return downloadFile(data?.client_id || clientId || user?.organization_id, item.id, item.name)
  }

  const listing = results
    ? { folders: results.folders, files: results.files, shared: [] }
    : mode === "trash"
      ? { folders: trash?.folders || [], files: trash?.files || [], shared: [] }
      : { folders: data?.folders || [], files: data?.files || [], shared: data?.shared_with_me || [] }

  const items = [...listing.folders, ...listing.files]
  const busy = loading || (mode === "trash" && trash === null)
  const readOnly = data?.access === "view"

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Files"
        subtitle="Everything the organization keeps that does not belong to one task. Folders are what get shared."
        actions={
          <Stack direction="row" spacing={1}>
            {can("drive", "create_folder") && mode === "browse" && !readOnly ? (
              <Button variant="outlined" startIcon={<NewFolderIcon />} onClick={() => setCreating(true)}>
                New folder
              </Button>
            ) : null}
            {can("drive", "upload") && mode === "browse" && !readOnly ? (
              <Button variant="contained" startIcon={<UploadIcon />} onClick={() => fileInput.current?.click()}>
                Upload
              </Button>
            ) : null}
          </Stack>
        }
      />

      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        onChange={(event) => {
          uploadFiles([...event.target.files])
          event.target.value = ""
        }}
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {notice ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      ) : null}

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            {isBackOffice && clients.length > 0 ? (
              <ClientSelect
                clients={clients}
                value={clientId}
                onChange={pickClient}
                emptyLabel="Our own files"
                label="Client"
                sx={{ minWidth: 200 }}
              />
            ) : null}

            <TextField
              size="small"
              label="Search"
              placeholder="Name of a file or folder"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ minWidth: 220 }}
            />

            <Box sx={{ flex: 1 }} />

            <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_e, value) => value && (value === "trash" ? openTrash() : setMode("browse"))}>
              <ToggleButton value="browse">Browse</ToggleButton>
              <ToggleButton value="trash">
                <TrashIcon sx={{ fontSize: 16, mr: 0.5 }} />
                Trash
              </ToggleButton>
            </ToggleButtonGroup>

            <ToggleButtonGroup size="small" exclusive value={view} onChange={(_e, value) => value && setView(value)}>
              <ToggleButton value="grid" aria-label="Grid">
                <GridIcon sx={{ fontSize: 18 }} />
              </ToggleButton>
              <ToggleButton value="list" aria-label="List">
                <ListIcon sx={{ fontSize: 18 }} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {mode === "browse" && !results ? (
            <Breadcrumbs sx={{ mt: 1.5 }} separator="›">
              <Crumb onClick={() => goTo(0)} icon={HomeIcon}>
                All files
              </Crumb>
              {(data?.breadcrumb || []).map((crumb, index, all) =>
                index === all.length - 1 ? (
                  <Typography key={crumb.id} variant="body2" fontWeight={700}>
                    {crumb.name}
                  </Typography>
                ) : (
                  <Crumb key={crumb.id} onClick={() => goTo(crumb.id)}>
                    {crumb.name}
                  </Crumb>
                ),
              )}
            </Breadcrumbs>
          ) : null}

          {results ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
              {items.length === 0
                ? `Nothing matches "${results.term}"`
                : `${items.length} match${items.length === 1 ? "" : "es"} for "${results.term}", across every folder you can open`}
            </Typography>
          ) : null}
        </CardContent>
      </Card>

      {uploads.length > 0 ? (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Stack spacing={1}>
              {uploads.map((ticket) => (
                <Box key={ticket.id}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={14} />
                    <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                      {ticket.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {ticket.percent}%
                    </Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={ticket.percent} sx={{ mt: 0.5, height: 4, borderRadius: 2 }} />
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {readOnly && mode === "browse" ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          This folder was shared with you to read. You can open and download what is in it.
        </Alert>
      ) : null}

      <DropZone
        onFiles={uploadFiles}
        disabled={!can("drive", "upload") || mode === "trash" || Boolean(results) || readOnly}
        label="Drop files here to upload them into this folder"
      >
        {busy ? (
          <Card>
            <CardContent>
              <LoadingRows rows={4} />
            </CardContent>
          </Card>
        ) : items.length === 0 && listing.shared.length === 0 ? (
          <Card>
            <EmptyState
              icon={mode === "trash" ? TrashIcon : FolderIcon}
              title={mode === "trash" ? "The trash is empty" : results ? "Nothing matches" : "Nothing here yet"}
              description={
                mode === "trash"
                  ? "Anything you delete waits here until you empty it."
                  : results
                    ? "Search looks at every folder you can open, so this is not a permissions problem — there is nothing by that name."
                    : "Drop a file onto this page, or make a folder to put one in."
              }
            />
          </Card>
        ) : view === "grid" ? (
          <GridView
            items={items}
            onOpen={open}
            onMenu={(event, item) => setMenu({ anchor: event.currentTarget, item })}
            onShare={setSharing}
            mode={mode}
          />
        ) : (
          <ListView
            items={items}
            onOpen={open}
            onMenu={(event, item) => setMenu({ anchor: event.currentTarget, item })}
            onShare={setSharing}
            mode={mode}
          />
        )}
      </DropZone>

      {/* Shared with you, at the root only: further in, everything under the
          folder you opened is already in the list above. */}
      {mode === "browse" && !results && folderId === 0 && listing.shared.length > 0 ? (
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <PeopleIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="h3" component="h2">
              Shared with you
            </Typography>
          </Stack>
          {view === "grid" ? (
            <GridView items={listing.shared} onOpen={open} onMenu={(event, item) => setMenu({ anchor: event.currentTarget, item })} onShare={setSharing} mode={mode} />
          ) : (
            <ListView items={listing.shared} onOpen={open} onMenu={(event, item) => setMenu({ anchor: event.currentTarget, item })} onShare={setSharing} mode={mode} />
          )}
        </Box>
      ) : null}

      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}>
        {mode === "trash"
          ? [
              <MenuItem
                key="restore"
                onClick={() => {
                  restore(menu.item)
                  setMenu(null)
                }}
              >
                <ListItemIcon>
                  <RestoreIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Put it back</ListItemText>
              </MenuItem>,
              <MenuItem
                key="purge"
                onClick={() => {
                  const item = menu.item
                  setMenu(null)
                  purge(item)
                }}
              >
                <ListItemIcon>
                  <TrashIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Delete for good</ListItemText>
              </MenuItem>,
            ]
          : [
              menu?.item?.kind === "file" ? (
                <MenuItem
                  key="download"
                  onClick={() => {
                    open(menu.item)
                    setMenu(null)
                  }}
                >
                  <ListItemIcon>
                    <DownloadIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Download</ListItemText>
                </MenuItem>
              ) : null,
              can("drive", "rename") ? (
                <MenuItem
                  key="rename"
                  onClick={() => {
                    setRenaming({ item: menu.item, draft: menu.item.name })
                    setMenu(null)
                  }}
                >
                  <ListItemIcon>
                    <RenameIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Rename</ListItemText>
                </MenuItem>
              ) : null,
              can("drive", "move") ? (
                <MenuItem
                  key="move"
                  onClick={() => {
                    setMoving(menu.item)
                    setMenu(null)
                  }}
                >
                  <ListItemIcon>
                    <MoveIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Move to…</ListItemText>
                </MenuItem>
              ) : null,
              can("drive", "share") ? (
                <MenuItem
                  key="share"
                  onClick={() => {
                    setSharing(menu.item)
                    setMenu(null)
                  }}
                >
                  <ListItemIcon>
                    <ShareIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Share…</ListItemText>
                </MenuItem>
              ) : null,
              can("drive", "delete") ? (
                <MenuItem
                  key="trash"
                  onClick={() => {
                    trashItem(menu.item)
                    setMenu(null)
                  }}
                >
                  <ListItemIcon>
                    <TrashIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Move to trash</ListItemText>
                </MenuItem>
              ) : null,
            ].filter(Boolean)}
      </Menu>

      {/* One inline field rather than a dialog: naming a folder is one word and
          a modal for it is three interactions where one would do. */}
      {creating ? (
        <Card sx={{ mt: 2, ...rise() }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center">
              <NewFolderIcon color="action" />
              <TextField
                autoFocus
                size="small"
                label="Folder name"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") createFolder()
                  if (event.key === "Escape") setCreating(false)
                }}
                sx={{ flex: 1, maxWidth: 320 }}
              />
              <Button variant="contained" onClick={createFolder} disabled={!newName.trim()}>
                Create
              </Button>
              <Button color="inherit" onClick={() => setCreating(false)}>
                Cancel
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {renaming ? (
        <Card sx={{ mt: 2, ...rise() }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center">
              <RenameIcon color="action" />
              <TextField
                autoFocus
                size="small"
                label={`Rename ${renaming.item.kind}`}
                value={renaming.draft}
                onChange={(event) => setRenaming({ ...renaming, draft: event.target.value })}
                onKeyDown={(event) => {
                  if (event.key === "Enter") rename()
                  if (event.key === "Escape") setRenaming(null)
                }}
                sx={{ flex: 1, maxWidth: 360 }}
              />
              <Button variant="contained" onClick={rename} disabled={!renaming.draft.trim()}>
                Save
              </Button>
              <Button color="inherit" onClick={() => setRenaming(null)}>
                Cancel
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      <ShareDialog
        item={sharing}
        clientId={clientId}
        storeLabel={clientId ? clients.find((client) => String(client.id) === String(clientId))?.label : user?.organization_label}
        onClose={() => setSharing(null)}
        onChanged={load}
      />
      <MoveDialog
        item={moving}
        clientId={clientId}
        onClose={() => setMoving(null)}
        onMoved={() => {
          setMoving(null)
          load()
        }}
      />
    </Box>
  )
}

function Crumb({ children, onClick, icon: Icon }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        appearance: "none",
        border: 0,
        background: "none",
        p: 0,
        font: "inherit",
        fontSize: 14,
        cursor: "pointer",
        color: "primary.dark",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        "&:hover": { textDecoration: "underline" },
      }}
    >
      {Icon ? <Icon sx={{ fontSize: 16 }} /> : null}
      {children}
    </Box>
  )
}

/** The colour of one row's glyph. A folder is one thing; a file is six. */
function tintFor(item) {
  if (item.kind === "folder") return "#e9a13b"
  return ICON_TINT[fileKind(item.mime_type, item.name)] || ICON_TINT.file
}

function GridView({ items, onOpen, onMenu, onShare, mode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)", lg: "repeat(5, 1fr)" },
      }}
    >
      {items.map((item, index) => {
        const tint = tintFor(item)
        return (
          <Card
            key={`${item.kind}-${item.id}`}
            sx={{ ...INTERACTIVE_CARD, ...rise(index), cursor: "pointer" }}
            onDoubleClick={() => onOpen(item)}
          >
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                <Box
                  onClick={() => onOpen(item)}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: `${tint}22`,
                    color: tint,
                    flexShrink: 0,
                    transition: transition("transform", MOTION.fast),
                    "&:hover": { transform: "scale(1.06)" },
                  }}
                >
                  {item.kind === "folder" ? <FolderIcon /> : <FileGlyph kind={fileKind(item.mime_type, item.name)} />}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Tooltip title={item.name} describeChild>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      onClick={() => onOpen(item)}
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        wordBreak: "break-word",
                      }}
                    >
                      {item.name}
                    </Typography>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {item.kind === "folder"
                      ? formatDate(mode === "trash" ? item.trashed_at : item.created_at)
                      : formatBytes(item.size_bytes)}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={(event) => onMenu(event, item)} aria-label={`Actions for ${item.name}`}>
                  <MoreIcon fontSize="small" />
                </IconButton>
              </Stack>

              {item.share_count > 0 || item.shared_access ? (
                /* Opens who-has-access, the way a shared drive's share badge
                   does — the question it raises is "who?", and the answer is
                   one click from the badge rather than three menus away. */
                <Chip
                  size="small"
                  icon={<PeopleIcon sx={{ fontSize: "13px !important" }} />}
                  label={item.shared_access ? (item.shared_access === "edit" ? "Can edit" : "Read only") : `Shared with ${item.share_count}`}
                  variant="outlined"
                  clickable
                  onClick={(event) => {
                    event.stopPropagation()
                    onShare(item)
                  }}
                  aria-label={`Who has access to ${item.name}`}
                  sx={{ mt: 1, height: 20, fontSize: 10 }}
                />
              ) : null}
            </CardContent>
          </Card>
        )
      })}
    </Box>
  )
}

function ListView({ items, onOpen, onMenu, onShare, mode }) {
  return (
    <Card>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell sx={{ width: 160 }}>Owner</TableCell>
            <TableCell sx={{ width: 120 }}>{mode === "trash" ? "Deleted" : "Added"}</TableCell>
            <TableCell sx={{ width: 100 }} align="right">
              Size
            </TableCell>
            <TableCell sx={{ width: 56 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => {
            const tint = tintFor(item)
            return (
              <TableRow key={`${item.kind}-${item.id}`} hover>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ color: tint, display: "flex" }}>
                      {item.kind === "folder" ? <FolderIcon fontSize="small" /> : <FileGlyph kind={fileKind(item.mime_type, item.name)} small />}
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => onOpen(item)}
                      sx={{
                        appearance: "none",
                        border: 0,
                        background: "none",
                        p: 0,
                        font: "inherit",
                        fontWeight: 600,
                        textAlign: "left",
                        cursor: "pointer",
                        color: "text.primary",
                        "&:hover": { color: "primary.dark", textDecoration: "underline" },
                      }}
                    >
                      {item.name}
                    </Box>
                    {item.share_count > 0 || item.shared_access ? (
                      <Tooltip title={item.share_count > 0 ? `Shared with ${item.share_count} — see who` : "Shared with you — see who else"}>
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation()
                            onShare(item)
                          }}
                          aria-label={`Who has access to ${item.name}`}
                          sx={{ p: 0.25 }}
                        >
                          <PeopleIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <UserAvatar name={item.created_by_name || item.uploaded_by_name} size={22} tooltip={false} />
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {item.created_by_name || item.uploaded_by_name || "—"}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(mode === "trash" ? item.trashed_at : item.created_at)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="text.secondary">
                    {item.kind === "folder" ? "—" : formatBytes(item.size_bytes)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={(event) => onMenu(event, item)} aria-label={`Actions for ${item.name}`}>
                    <MoreIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Card>
  )
}

/** A glyph per file kind. One import each, so the bundle carries six icons. */
function FileGlyph({ kind, small = false }) {
  const size = small ? "small" : "medium"
  if (kind === "image") return <ImageGlyph fontSize={size} />
  if (kind === "pdf") return <PdfGlyph fontSize={size} />
  if (kind === "sheet") return <SheetGlyph fontSize={size} />
  if (kind === "doc") return <DocGlyph fontSize={size} />
  if (kind === "archive") return <ArchiveGlyph fontSize={size} />
  return <FileGlyphDefault fontSize={size} />
}
